/**
 * Resolve a `(schemaName, version[, objectId])` triple or a raw pathname to a
 * navigable URL or a display label, on top of the `paths` builders.
 */
import { paths } from "./pathConstants";

/** Static paths from `paths` that are plain strings (not functions). */
type StaticPath = {
  [K in keyof typeof paths]: (typeof paths)[K] extends string ? (typeof paths)[K] : never;
}[keyof typeof paths];

/** Route labels for display in the command palette and breadcrumbs. */
const routeLabels: Record<StaticPath, string> = {
  [paths.dashboard]: "Dashboard",
  [paths.businessObjects]: "Business Objects",
  [paths.schemaRegistry]: "Workbench",
  [paths.schemaRegistryModel]: "Schema Model Editor",
  [paths.schemaRegistryGraph]: "Schema Graph",
  [paths.viewConfiguration]: "View Configuration",
  [paths.documents]: "Documents",
  [paths.libraries]: "Libraries",
  [paths.librariesImages]: "Images",
  [paths.imagesPick]: "Add a picture",
  [paths.issues]: "Issues",
  [paths.settings]: "Settings",
  [paths.recordLoader]: "Record Loader",
  [paths.flavourProvisioning]: "Flavour Provisioning",
  [paths.pageComposer]: "Page composer",
  [paths.workflowNew]: "New Workflow",
  [paths.requests]: "Requests",
};

/**
 * Resolve the canonical list URL for a `(schemaName, version)` pair. There
 * is no per-domain override anymore — every schema lives at the same path
 * shape (`/{schemaName}/{version}`). Kept as a function so consumers don't
 * hand-build URLs and so we can change the shape in one place if needed.
 */
export const resolveResourceListPath = (schemaName: string, version: string): string =>
  paths.resourceList(schemaName, version);

/**
 * Record-URL query that carries a list slot's `detailPage` (NavPage key).
 * Not `page`: collection tables already own `?page=` for pagination, and
 * writing a NavPage key there would be overwritten the first time a nested
 * table paginates.
 */
export const RECORD_PAGE_SEARCH_PARAM = "detailPage";

/** In-app path the record was opened from, so the breadcrumb survives a reload or a shared link. */
export const RECORD_ORIGIN_SEARCH_PARAM = "from";

/** An in-app path, or undefined. Rejects protocol-relative URLs and control characters so `?from=` cannot leave the app. */
const appPathOrUndefined = (value: string | null | undefined): string | undefined => {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) {
    return undefined;
  }

  // eslint-disable-next-line no-control-regex -- control characters are what this rejects
  return /[\u0000-\u001f\u007f]/.test(value) ? undefined : value;
};

export const readRecordOrigin = (params: URLSearchParams): string | undefined =>
  appPathOrUndefined(params.get(RECORD_ORIGIN_SEARCH_PARAM));

export const resolveResourceDetailPath = (
  schemaName: string,
  version: string,
  objectId: string,
  options?: { page?: string; from?: string },
): string => {
  const path = paths.resourceDetail(schemaName, version, objectId);
  const page = options?.page?.trim();
  const from = appPathOrUndefined(options?.from);
  const params = new URLSearchParams();

  if (page) {
    params.set(RECORD_PAGE_SEARCH_PARAM, page);
  }

  if (from) {
    params.set(RECORD_ORIGIN_SEARCH_PARAM, from);
  }

  const query = params.toString();

  return query ? `${path}?${query}` : path;
};

/** A WorkflowDefinition row's own version: its `version` field, else the suffix of its
 *  `definitionKey@version` pk. */
const workflowDefinitionVersion = (
  record: Record<string, unknown> | undefined,
  pk: string,
): string | undefined => {
  const version = record?.["version"];

  if (typeof version === "string" && version.length > 0) {
    return version;
  }

  const separatorIndex = pk.indexOf("@");

  return separatorIndex > 0 ? pk.slice(separatorIndex + 1) || undefined : undefined;
};

/**
 * Resolve the URL to open when the user clicks a record.
 *
 * For schemas with a bespoke route — `WorkflowDefinition` → the authoring/run
 * page, `WorkflowRun` → the run-detail page (the view the old `/workflows`
 * "Runs" tab linked to) — this returns that route instead of the generic
 * resource-detail (properties) path. All other schemas fall through to
 * `resolveResourceDetailPath`.
 *
 * A WorkflowDefinition row is one VERSION of its key (the pk is
 * `definitionKey@version`), so the page it opens is pinned to that version —
 * unpinned, the page shows the key's default version, and a 1.1.0 draft row
 * would open on 1.0.0.
 *
 * @param schemaName  - Schema identifier (e.g. `"WorkflowDefinition"`).
 * @param version     - Schema version string (e.g. `"1.0.0"`).
 * @param record      - Raw record fields from the collection row/card.
 * @param fallbackId  - Primary-key string used when no bespoke route applies.
 */
export const resolveRecordOpenPath = (
  schemaName: string,
  version: string,
  record: Record<string, unknown> | undefined,
  fallbackId: string,
  options?: { page?: string; from?: string },
): string => {
  if (schemaName === "WorkflowDefinition") {
    const definitionKey = record?.["definitionKey"];

    if (typeof definitionKey === "string" && definitionKey.length > 0) {
      return paths.workflowDefinition(definitionKey, {
        version: workflowDefinitionVersion(record, fallbackId),
      });
    }
  }

  if (schemaName === "WorkflowRun") {
    const runId = record?.["runId"];

    if (typeof runId === "string" && runId.length > 0) {
      return paths.workflowRunDetail(runId);
    }
  }

  return resolveResourceDetailPath(schemaName, version, fallbackId, options);
};

/**
 * Inverse of {@link resolveRecordOpenPath} for sidebar active-state. The bespoke
 * workflow pages live under `/workflows/*` — a different URL subtree than the
 * `/WorkflowDefinition|WorkflowRun` collection URLs the nav items point at — so a
 * plain prefix match misses them. This maps such a pathname back to the schema
 * whose nav item should light up. Returns null for non-bespoke paths (the prefix
 * match on the nav item's own URL handles those).
 */
export const resolveActiveResourceSchema = (pathname: string): string | null => {
  // Order matters: the run-detail subtree is nested under `/workflows`.
  if (pathname.startsWith("/workflows/runs")) {
    return "WorkflowRun";
  }

  // Studio catalogue and Activity pins (`namespace.name@version`) share the
  // `/workflows` prefix with product definition pages. Do not light up the
  // product WorkflowDefinition nav for those.
  if (pathname === "/workflows" || pathname === "/workflows/") {
    return null;
  }
  const workflowSegment = pathname.match(/^\/workflows\/([^/?#]+)/)?.[1];
  if (workflowSegment) {
    let decoded = workflowSegment;
    try {
      decoded = decodeURIComponent(workflowSegment);
    } catch {
      // keep raw segment
    }
    if (decoded.includes("@") || workflowSegment.includes("%40")) {
      return null;
    }
    if (decoded === "new") {
      return null;
    }
  }

  if (pathname.startsWith("/workflows")) {
    return "WorkflowDefinition";
  }

  return null;
};

/** Resolve a label for any pathname, including parameterized routes. */
export const getRouteLabel = (pathname: string): string | null => {
  // Exact match first
  const label = (routeLabels as Record<string, string>)[pathname];

  if (label) {
    return label;
  }

  if (pathname === "/colours-palettes" || pathname.startsWith("/colours-palettes/")) {
    return "Colours & Palettes";
  }

  if (pathname === "/materials" || pathname.startsWith("/materials/")) {
    return "Materials";
  }

  if (pathname === "/partners" || pathname.startsWith("/partners/")) {
    return "Partners";
  }

  if (pathname === "/styles" || pathname.startsWith("/styles/")) {
    return "Styles";
  }

  if (pathname === "/requests" || pathname.startsWith("/requests/")) {
    return "Requests";
  }

  // Configuration Workbench Entity Hub under Schema Registry.
  const schemaHubMatch = pathname.match(/^\/schema-registry\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/);
  if (schemaHubMatch) {
    const segment = decodeURIComponent(schemaHubMatch[1]);
    if (segment !== "model" && segment !== "graph") {
      return segment;
    }
  }

  // Business-objects detail routes: the schema-detail (tabbed) page at
  // `/business-objects/:schema/:version[/tab]` reads as the schema name; the
  // nested `/:objectId` compat route (which redirects to the canonical record
  // URL) reads as a record. Tab subpaths (`records`, `views`, `attributes`)
  // are not record ids.
  const boMatch = pathname.match(/^\/business-objects\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/);
  const schemaDetailTabSegments = new Set(["records", "views", "attributes"]);

  if (boMatch) {
    const [, schemaName, , thirdSegment] = boMatch;
    const decoded = decodeURIComponent(schemaName);

    if (thirdSegment && !schemaDetailTabSegments.has(thirdSegment)) {
      return `${decoded} Record`;
    }

    return decoded;
  }

  // Canonical resource routes (`/{schemaName}/{version}[/:objectId]`). We
  // distinguish from `/business-objects/...` by the absence of the
  // `business-objects` prefix; the schemaName segment is captured as the
  // first segment of the path.
  const resourceMatch = pathname.match(/^\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/);

  if (resourceMatch) {
    const [, first, second, third] = resourceMatch;
    // Filter out the schema-registry sub-paths (`/schema-registry/model`).
    // `first` here is the schemaName.
    if (first !== "schema-registry") {
      const decoded = decodeURIComponent(first);
      // Heuristic: `second` is the version (looks like `1.0.0`), `third`
      // (if present) is the record id.
      if (/^\d/.test(second)) {
        return third ? `${decoded} Record` : `${decoded}`;
      }
    }
  }

  return null;
};
